import Cookies from 'js-cookie';

export enum EWebViewActions {
  CLOSE_WEB_VIEW = 'close_webview',
  UPDATE_APPBAR = 'update_appbar',
  OPEN_DEEPLINK = 'open_deeplink',
  FETCH_USER_LOCATION = 'fetch_user_location',
  OPEN_SETTING_PAGE = 'open_settings_page',
  ADD_COOKIE = 'add_cookie'
}

export interface IAppBarMenuItem {
  icon_url?: string;
  cta_text?: string;
  action_name?: string;
  redirect_url?: string;
}

export interface IAppBarConfig {
  background_color?: string;
  back_button_color?: string;
  is_appbar_transparent?: boolean;
  ignore_blur?: boolean;
  ignoreSafeArea?: boolean;
  show_back_button?: boolean;
  title?: string;
  title_color?: string;
  status_icon_brightness?: string;
  deeplink?: string;
  menu_items?: IAppBarMenuItem[];
}

export interface IAppBarMenuItemInput {
  iconUrl?: string;
  actionName?: string;
  redirectUrl?: string;
  ctaText?: string;
}

export interface IAppBarMenuConfig {
  backgroundColor?: string;
  backButtonColor?: string;
  isAppbarTransparent?: boolean;
  ignoreBlur?: boolean;
  statusIconBrightness?: string;
  menuItems?: IAppBarMenuItemInput[];
}

export interface IAppMenuBarConfig {
  iconUrl?: string,
  redirectUrl?: string,
  actionName?: string,
  ctaText?: string,
  backgroundColor?: string,
  backButtonColor?: string,
  isAppbarTransparent?: boolean,
  ignore_blur?: boolean,
  statusIconBrightness?: string
}

export interface IWebViewActionData {
  action: EWebViewActions;
  action_value?:
    | IAppBarConfig
    | IAppMenuBarConfig
    | { url?: string }
    | { cookie_name: string; cookie_value: string }
    | null;
}

export interface IWebViewLocationResponse {
  lat: number;
  long: number;
  accuracy?: number;
  status: string;
}

const canUseWebViewHandler = () =>
  Boolean(Cookies.get('webview')) &&
  typeof window !== 'undefined' &&
  Boolean((window as any)?.flutter_inappwebview?.callHandler);

const parseWebViewResponse = <T,>(response: any): T => {
  if (typeof response === 'string') {
    return JSON.parse(response) as T;
  }

  return response as T;
};

export function UseWebviewCallback(
  data: IWebViewActionData,
  nativeWebFunc: () => void,
  getRes?: (res: any) => void
) {
  try {
    if (data?.action && canUseWebViewHandler()) {
      (window as any)
        .flutter_inappwebview.callHandler('WebViewConnect', JSON.stringify(data))
        .then((res: any) => {
          getRes?.(res);
        })
        .catch(() => {
          nativeWebFunc();
        });
      return;
    }

    nativeWebFunc();
  } catch (e) {
    nativeWebFunc();
  }
}


export const closeWebView = (fallback: () => void) => {
  UseWebviewCallback(
    {
      action: EWebViewActions.CLOSE_WEB_VIEW,
      action_value: null,
    },
    fallback
  );
};

export const openHomeScreen = () => {
  UseWebviewCallback(
    {
      action: EWebViewActions.OPEN_DEEPLINK,
      action_value : {
        deeplink : "acko.com://app_home"
      }
    },
    () => {}
  );
};

export const openWebPageDeepLink = (url: string) => {
  UseWebviewCallback(
    {
      action: EWebViewActions.OPEN_DEEPLINK,
      action_value: {
        deeplink: `acko.com://web_page_v2?url=${encodeURIComponent(url)}`,
      },
    },
    () => {
      if (typeof window !== "undefined") {
        window.location.href = url;
      }
    }
  );
};

export const openSettingPage = () => {
  UseWebviewCallback(
    {
      action: EWebViewActions.OPEN_SETTING_PAGE,
      action_value : null
    },
    () => {}
  );
}

export const addCookieToWebView = (
  cookieName: string,
  cookieValue: string
): Promise<void> => {
  return new Promise((resolve) => {
    UseWebviewCallback(
      {
        action: EWebViewActions.ADD_COOKIE,
        action_value: {
          cookie_name: cookieName,
          cookie_value: cookieValue,
        },
      },
      () => resolve(),
      () => resolve()
    );
  });
};

export const updateAppBar = (config: IAppBarConfig | IAppMenuBarConfig | IAppBarMenuConfig) => {
  UseWebviewCallback(
    {
      action: EWebViewActions.UPDATE_APPBAR,
      action_value: config,
    },
    () => {}
  );
};

export const fetchUserLocationFromWebView = async (): Promise<IWebViewLocationResponse> => {
  if (!canUseWebViewHandler()) {
    throw new Error('WebView location handler is unavailable.');
  }

  const rawResponse = await (window as any).flutter_inappwebview.callHandler(
    'WebViewConnect',
    JSON.stringify({ action: EWebViewActions.FETCH_USER_LOCATION })
  );

  return parseWebViewResponse<IWebViewLocationResponse>(rawResponse);
};
